import { useId } from 'react';
import { DropEvent } from 'react-aria';
import { Button as AriaButton, DropZone, FileTrigger } from 'react-aria-components';
import { Plural, Trans } from '@lingui/react/macro';

import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconAdd from '@/icons/Add.svg';
import IconCamera from '@/icons/Camera.svg';
import IconFileVerification from '@/icons/FileVerification.svg';
import IconPhoneUpload from '@/icons/PhoneUpload.svg';
import IconUpload from '@/icons/Upload.svg';
import { createBEMClasses } from '@/utils/classname';
import useViewPort from '@/utils/useViewport';

import Menu from '../Menu';
import { MenuItem } from '../Menu/Menu';
import Stack from '../Stack';

import QrCodeUpload from './QrCodeUpload';

import './InputFile.css';

const { block, element } = createBEMClasses('input-file');

export type InputFileProps = {
  icon?: React.ReactNode;
  prompt?: React.ReactNode;
  ariaLabel: string;
  type?: 'photo' | 'file';
  onUpload?: (files: File[]) => void;
  allowsMultiple?: boolean;
  variant?: 'default' | 'square';
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
  isInvalid?: boolean;
  isDisabled?: boolean;
  onUploadFromPhone?: () => void;
  uploadFromPhoneButtonIconOnly?: boolean;
  isLoadingOnUploadFromPhone?: boolean;
};

const InputFile = ({
  prompt,
  ariaLabel,
  type = 'file',
  onUpload,
  allowsMultiple = true,
  variant = 'default',
  size = 'large',
  icon,
  isInvalid,
  isDisabled,
  onUploadFromPhone,
  uploadFromPhoneButtonIconOnly,
  isLoadingOnUploadFromPhone,
}: InputFileProps) => {
  const browseFilesButtonId = useId();
  const { isMobile, isTablet } = useViewPort();
  const isMobileOrTablet = isMobile || isTablet;

  const handleDropEvent = async (e: DropEvent) => {
    const files = await Promise.all(
      e.items
        .filter((item) => item.kind === 'file')
        .filter((item) => ACCEPTED_FILE_TYPES[type].includes(item.type))
        .map(async (item) => await item.getFile())
    );

    if (files.length) {
      if (allowsMultiple) {
        onUpload?.(files);
      } else {
        onUpload?.([files[0]]);
      }
    }
  };

  const handleSelectEvent = (fileList: FileList | null) => {
    const files = Array.from(fileList ?? []);

    if (files.length) {
      onUpload?.(files);
    }
  };

  const hasActionsMenu = variant === 'square' && onUploadFromPhone;

  return (
    <DropZone
      className={block({ variant, size, invalid: isInvalid })}
      onDrop={(e) => {
        handleDropEvent(e);
      }}
      /**
       * For unknown reason, Safari returns an empty `types` array when a file is dropped.
       * Therefore, we cannot use this method as a reliable way to determine if all the dropped files are valid.
       * It is still an helpful visual helper for the user that works in most cases.
       *
       * Note: We keep `.some` instead of `.every` to handle the cases where the user uploads a mix of valid and invalid files.
       * In such cases, they will be filtered in the `handleDropEvent` method.
       */
      getDropOperation={(types) =>
        ACCEPTED_FILE_TYPES[type].some((type) => types.has(type)) ? 'copy' : 'cancel'
      }
      isDisabled={isDisabled}
    >
      <FileTrigger
        onSelect={handleSelectEvent}
        acceptedFileTypes={ACCEPTED_FILE_TYPES[type]}
        allowsMultiple={allowsMultiple}
      >
        <AriaButton
          id={browseFilesButtonId}
          aria-label={ariaLabel}
          isDisabled={isDisabled}
          className={element('button')}
          style={{ display: hasActionsMenu ? 'none' : undefined }}
        />
      </FileTrigger>
      {hasActionsMenu ? (
        <Menu
          trigger={
            <AriaButton
              aria-label={ariaLabel}
              isDisabled={isDisabled}
              className={element('button')}
            >
              <div className={element('button-zone')}>{icon ?? <IconAdd />}</div>
            </AriaButton>
          }
          placement="bottom left"
          onAction={(action: string) => {
            if (action === 'browse_files') {
              /**
               * Here we have ReactAriaButton link to this label by htmlFor attribute but there is a know issue between ReactAriaButton onPress method and
               * Firefox's way of handling virtual/synthetic click (https://github.com/adobe/react-spectrum/pull/3614 & https://bugzilla.mozilla.org/show_bug.cgi?id=1796300).
               *
               * So this workaround simulate an other click using React component id attribute using useId hook to ensure to have a unique id for each instance of component to trigger the FileTrigger component's button
               */
              const browseFilesButton = document.getElementById(browseFilesButtonId);

              if (browseFilesButton) {
                browseFilesButton.click();
              }
            } else if (action === 'qr_code') {
              onUploadFromPhone();
            }
          }}
        >
          <MenuItem id="browse_files">
            <Stack row gap="0.5rem" alignItems="center" style={{ cursor: 'pointer' }}>
              <IconFileVerification style={{ fontSize: '1.25rem' }} />
              <Trans id="design-system.input-file.square.file-trigger.button-label">
                Browse files
              </Trans>
            </Stack>
          </MenuItem>
          <MenuItem id="qr_code">
            <Stack row gap="0.5rem" alignItems="center">
              <IconPhoneUpload style={{ fontSize: '1.25rem' }} />
              <Trans id="design-system.qr-code.button-label">Upload from a phone</Trans>
            </Stack>
          </MenuItem>
        </Menu>
      ) : (
        <div className={element('button-zone')}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
          <label
            onClick={() => {
              /**
               * Here we have ReactAriaButton link to this label by htmlFor attribute but there is a know issue between ReactAriaButton onPress method and
               * Firefox's way of handling virtual/synthetic click (https://github.com/adobe/react-spectrum/pull/3614 & https://bugzilla.mozilla.org/show_bug.cgi?id=1796300).
               *
               * So this workaround simulate an other click using React component id attribute using useId hook to ensure to have a unique id for each instance of component to trigger the FileTrigger component's button
               */
              const browseFilesButton = document.getElementById(browseFilesButtonId);

              if (browseFilesButton) {
                browseFilesButton.click();
              }
            }}
          >
            {icon ?? (isMobileOrTablet ? <IconCamera /> : <IconUpload />)}
            {prompt !== null ? (
              <Paragraph className={element('prompt')}>
                {prompt ?? (
                  <Trans id="design-system.input-file.file.label">
                    Drop <Plural value={allowsMultiple ? 5 : 1} one="file" other="files" /> or{' '}
                    <InlineText weight="medium">upload</InlineText>
                  </Trans>
                )}
              </Paragraph>
            ) : null}
          </label>
          {onUploadFromPhone && (
            <>
              <hr
                style={{
                  display: 'inline-block',
                  width: '1px',
                  height: '16px',
                  backgroundColor: '#D9D9D9',
                  margin: '0 0.75rem',
                }}
                aria-hidden="true"
              />
              <QrCodeUpload
                onUploadFromPhone={onUploadFromPhone}
                isDisabled={isDisabled || isLoadingOnUploadFromPhone}
                size={size === 'large' || size === 'xlarge' || size === 'xxlarge' ? 'medium' : size}
                iconOnly={uploadFromPhoneButtonIconOnly}
              />
            </>
          )}
        </div>
      )}
    </DropZone>
  );
};

export const ACCEPTED_FILE_TYPES = {
  photo: ['image/jpeg', 'image/png', 'image/webp', 'image/jfif'],
  file: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/jfif'],
};

export default InputFile;
